import { StorageAreaListApiResponse } from '@navio/nsdk';
import { URLKeys } from 'noddi-async';
import { type FilterOptionSource, usePagination } from 'noddi-ui-web';
import { useDebounceValue } from 'noddi-util';
import { useCallback } from 'react';
import { toFilterOptionSource, useFilterOptionsGet } from '../useFilterOptionsGet';

const AREA_LABEL_COLLATOR = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

const NO_EXCLUDED_AREA_NAMES: readonly string[] = [];

/** Storage areas as filter options, searchable. For `unit.multiSelect`. */
export const useStorageAreaOptions = ({
  storageFacilityIds,
  serviceDepartmentIds,
  excludeAreaNames = NO_EXCLUDED_AREA_NAMES
}: {
  storageFacilityIds?: number[];
  serviceDepartmentIds?: number[];
  /** Adapter catch-alls etc. Exclude by exact area name at the options boundary. */
  excludeAreaNames?: readonly string[];
} = {}): FilterOptionSource<number> => {
  const { pageIndex, pageSize, search, setSearch } = usePagination();
  const debouncedSearch = useDebounceValue({ value: search, delay: 300 });

  const toOptions = useCallback(
    ({ results }: StorageAreaListApiResponse) => {
      const excludedNames = new Set(excludeAreaNames);
      return results
        .filter((area) => !excludedNames.has(area.name))
        .map((area) => ({
          label: area.name,
          value: area.id
        }))
        .sort((a, b) => AREA_LABEL_COLLATOR.compare(a.label, b.label));
    },
    [excludeAreaNames]
  );

  const query = useFilterOptionsGet({
    type: URLKeys.getStorageAreas,
    input: {
      pageIndex,
      pageSize,
      search: debouncedSearch || undefined,
      storageFacilityIds: storageFacilityIds && storageFacilityIds.length > 0 ? storageFacilityIds : undefined,
      serviceDepartmentIds: serviceDepartmentIds && serviceDepartmentIds.length > 0 ? serviceDepartmentIds : undefined
    },
    queryConfig: { select: toOptions }
  });

  return toFilterOptionSource(query, 'number', { search, onSearch: setSearch });
};
